import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { apiGet, apiPost } from '../../utils/api';
import '../../styles/philip-snat-runners.scss';

type Schedule = 'midnight' | 'noon';

interface Runner {
  schedule: Schedule;
  leagues: string[];
  configured: boolean;
}

type RunStatus = { kind: 'success' | 'error'; message: string };

const PhilipSnatRunners: React.FC = () => {
  const { t } = useTranslation();
  const [runners, setRunners] = useState<Runner[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningSchedule, setRunningSchedule] = useState<Schedule | null>(null);
  const [statuses, setStatuses] = useState<Partial<Record<Schedule, RunStatus>>>({});

  useEffect(() => {
    apiGet<Runner[]>('/admin/philip-snat/runners')
      .then(setRunners)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const scheduleLabels: Record<Schedule, string> = {
    midnight: t.admin.runnerMidnight,
    noon: t.admin.runnerNoon,
  };

  const handleRun = async (schedule: Schedule) => {
    setRunningSchedule(schedule);
    try {
      await apiPost(`/admin/philip-snat/runners/${schedule}/run`);
      setStatuses(prev => ({ ...prev, [schedule]: { kind: 'success', message: t.admin.runnerStarted } }));
    } catch (err) {
      console.error('Failed to start runner:', err);
      setStatuses(prev => ({ ...prev, [schedule]: { kind: 'error', message: t.admin.runnerFailed } }));
    } finally {
      setRunningSchedule(null);
    }
  };

  const configured = runners.every(r => r.configured);

  return (
    <div className="philip-snat-runners">
      <div className="philip-snat-runners__container">
        <h1 className="philip-snat-runners__title">{t.admin.philipSnatRunners}</h1>

        {loading ? (
          <p>{t.common.loading}</p>
        ) : (
          <>
            {!configured && (
              <p className="philip-snat-runners__notice">{t.admin.runnerNotConfigured}</p>
            )}

            <div className="philip-snat-runners__list">
              {runners.map(runner => {
                const runStatus = statuses[runner.schedule];
                return (
                  <section key={runner.schedule} className="philip-snat-runners__card">
                    <h2 className="philip-snat-runners__card-title">
                      {scheduleLabels[runner.schedule]}
                    </h2>

                    <div className="philip-snat-runners__leagues">
                      <span className="philip-snat-runners__leagues-label">
                        {t.admin.runnerLeagues}
                      </span>
                      {runner.leagues.length > 0 ? (
                        runner.leagues.map(league => (
                          <span key={league} className="philip-snat-runners__league-pill">
                            {league}
                          </span>
                        ))
                      ) : (
                        <span className="philip-snat-runners__empty">{t.admin.runnerNoLeagues}</span>
                      )}
                    </div>

                    <button
                      className="button_primary philip-snat-runners__button"
                      onClick={() => handleRun(runner.schedule)}
                      disabled={
                        !runner.configured ||
                        runner.leagues.length === 0 ||
                        runningSchedule !== null
                      }
                    >
                      {runningSchedule === runner.schedule ? t.admin.runnerStarting : t.admin.runNow}
                    </button>

                    {runStatus && (
                      <p
                        className={`philip-snat-runners__status philip-snat-runners__status--${runStatus.kind}`}
                      >
                        {runStatus.message}
                      </p>
                    )}
                  </section>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PhilipSnatRunners;
